import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Video, House } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal } from "./Reveal";
import { WHATSAPP_URL, GOOGLE_CALENDAR_URL, SERVICES } from "@/lib/svenData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WhatsAppModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", phone: "", service: SERVICES[0], date: "", time: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        phone: form.phone,
        service: form.service,
        preferred_date: form.date,
        preferred_time: form.time,
        source: "whatsapp",
      });
    } catch (err) {
      console.error("lead save failed", err);
    }
    const msg = [
      "Hello Sven Suits! I'd like to book a consultation.",
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Service: ${form.service}`,
      form.date && `Preferred date: ${form.date}`,
      form.time && `Preferred time: ${form.time}`,
    ].filter(Boolean).join("\n");
    window.open(`${WHATSAPP_URL}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.success("Opening WhatsApp with your details…");
    setSending(false);
    onOpenChange(false);
  };

  const inputCls = "w-full bg-white border border-[#e7e2da] rounded-sm px-4 py-3 text-sm font-light text-[#141414] focus:outline-none focus:border-[#c5a059] transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="whatsapp-booking-modal" className="bg-[#faf8f5] border-[#e7e2da] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-[#141414]">Connect via WhatsApp</DialogTitle>
          <DialogDescription className="font-light text-[#57534e]">
            Share a few details — we'll open WhatsApp with your message pre-filled.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input data-testid="whatsapp-form-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
          <input data-testid="whatsapp-form-phone" required placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} />
          <select data-testid="whatsapp-form-service" value={form.service} onChange={set("service")} className={inputCls}>
            {SERVICES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-4">
            <input data-testid="whatsapp-form-date" type="date" value={form.date} onChange={set("date")} className={inputCls} />
            <input data-testid="whatsapp-form-time" type="time" value={form.time} onChange={set("time")} className={inputCls} />
          </div>
          <button
            type="submit"
            data-testid="whatsapp-form-submit-btn"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#25D366] text-[#0b0b0c] font-mono-ov text-xs tracking-[0.15em] uppercase rounded-full hover:brightness-110 transition-all disabled:opacity-60"
          >
            <FaWhatsapp size={15} /> Continue to WhatsApp
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const CalendarModal = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="calendar-booking-modal" className="bg-[#faf8f5] border-[#e7e2da] sm:max-w-3xl p-0 overflow-hidden">
      <DialogHeader className="px-6 pt-6">
        <DialogTitle className="font-display text-2xl font-medium text-[#141414]">Schedule a Google Meet</DialogTitle>
        <DialogDescription className="font-light text-[#57534e]">
          Pick a slot below — a Google Meet invite lands in your inbox.
        </DialogDescription>
      </DialogHeader>
      <div className="px-6 pb-6">
        <iframe
          data-testid="calendar-embed-iframe"
          src={GOOGLE_CALENDAR_URL}
          title="Sven Suits — Google Calendar booking"
          className="w-full h-[60vh] border border-[#e7e2da] rounded-sm bg-white"
        />
        <a
          data-testid="calendar-open-new-tab"
          href={GOOGLE_CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 font-mono-ov text-[11px] tracking-[0.15em] uppercase text-[#99732b] hover:text-[#141414] transition-colors"
        >
          <ExternalLink size={13} /> Trouble loading? Open the booking page in a new tab
        </a>
      </div>
    </DialogContent>
  </Dialog>
);

export const Booking = () => {
  const [waOpen, setWaOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  return (
    <section id="book" data-testid="booking-hub-section" className="bg-[#0b0b0c] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline-label mb-5">Book a Consultation</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium text-[#faf8f5] mb-4 max-w-2xl">
            Two ways to begin. <em className="font-accent text-[#dfcca4]">Both start with a conversation.</em>
          </h2>
          <p className="text-sm font-light text-white/55 max-w-xl mb-14">
            In person at Santacruz or Bandra, at your home or office, or over video — choose whichever suits you.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          <Reveal>
            <div className="h-full bg-[#141416] border border-white/10 p-9 flex flex-col justify-between hover:border-[#25D366]/50 transition-colors duration-500 group">
              <div>
                <div className="h-12 w-12 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#25D366] mb-6 group-hover:scale-110 transition-transform">
                  <FaWhatsapp size={22} />
                </div>
                <h3 className="font-display text-xl text-[#faf8f5]">Connect via WhatsApp</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/55">
                  Tell us your name, service and preferred time — we'll continue the conversation
                  directly on WhatsApp.
                </p>
              </div>
              <button
                data-testid="open-whatsapp-modal-btn"
                onClick={() => setWaOpen(true)}
                className="mt-8 w-full py-3.5 bg-[#25D366] text-[#0b0b0c] font-mono-ov text-xs tracking-[0.15em] uppercase rounded-full hover:brightness-110 hover:scale-[1.02] transition-all duration-300"
              >
                Start on WhatsApp
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="h-full bg-[#141416] border border-white/10 p-9 flex flex-col justify-between hover:border-[#c5a059]/60 transition-colors duration-500 group">
              <div>
                <div className="h-12 w-12 rounded-full bg-[#c5a059]/10 flex items-center justify-center text-[#c5a059] mb-6 group-hover:scale-110 transition-transform">
                  <Video size={22} />
                </div>
                <h3 className="font-display text-xl text-[#faf8f5]">Schedule a Google Meet</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/55">
                  A virtual consultation from anywhere — pick a slot on our live calendar and
                  receive a Meet invite instantly.
                </p>
              </div>
              <button
                data-testid="open-calendar-modal-btn"
                onClick={() => setCalOpen(true)}
                className="mt-8 w-full py-3.5 border border-[#c5a059] text-[#dfcca4] font-mono-ov text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#c5a059] hover:text-[#0b0b0c] hover:scale-[1.02] transition-all duration-300"
              >
                Pick a Video Slot
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="h-full bg-[#141416] border border-white/10 p-9 flex flex-col justify-between hover:border-[#fbfbfc]/60 transition-colors duration-500 group">
              <div>
                <div className="h-12 w-12 rounded-full bg-[#fbfbfc]/10 flex items-center justify-center text-[#fbfbfc] mb-6 group-hover:scale-110 transition-transform">
                  <House size={22} />
                </div>
                <h3 className="font-display text-xl text-[#faf8f5]">Private Home Services <em className="font-display text-[#dfcca4]">We come to you.</em></h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/55">
                For clients in Mumbai and Thane, request a private home consultation 
                for fabric selection, measurements and styling. We will confirm the 
                appointment after checking availability.
                </p>
              </div>
              <button
                data-testid="open-calendar-modal-btn"
                onClick={() => setCalOpen(true)}
                className="mt-8 w-full py-3.5 border border-[#fbfbfc] text-[#dfcca4] font-mono-ov text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#fbfbfc] hover:text-[#0b0b0c] hover:scale-[1.02] transition-all duration-300"
              >
                Pick a Video Slot
              </button>
            </div>
          </Reveal>
        </div>
      </div>
      <WhatsAppModal open={waOpen} onOpenChange={setWaOpen} />
      <CalendarModal open={calOpen} onOpenChange={setCalOpen} />
    </section>
  );
};